import {
  Avatar,
  AvatarGroup,
  Button,
  Clickable,
  P,
} from '@fashionnovateam/nova-ui-toolkit';
import React from 'react';
import { DateTime } from 'luxon';
import { InspoTableColumns } from '../../types/TableColumns';
import { Priority } from '../../types/Inspo';

const mockData = [
  // todo: Remove this mock data once we have the actual API endpoints
  {
    id: '1',
    photos: [
      'https://cdn.shopify.com/s/files/1/0293/9277/files/11-19-24_S7_68_ZDF01F440029_BlackWash_CZ_DJ_11-33-22_43875_PXF.jpg',
      'https://cdn.shopify.com/s/files/1/0293/9277/products/08-31-21Studio4_RT_RL_15-25-39_121_FNMH880_BlackWash_8488_PB.jpg',
      'https://cdn.shopify.com/s/files/1/0293/9277/products/NOVABEAUTYD.T.F.PreciseBrowPencil-Blonde_mer.jpg',
    ],
    priority: {
      id: 'badge_priority',
      children: Priority.IMMEDIATE,
      contrast: 'low',
      color: 'red',
    },
    style_color: 'Blue/S001',
    division: 'Women',
    display_name: 'Summer Dress',
    alias: 'Beach Collection',
    key_details: 'Lightweight, breathable fabric',
    counter_sample_status: {
      id: 'badge_counter_sample_status',
      children: 'Pending',
      contrast: 'low',
      color: 'yellow',
    },
    counter_quote_status: {
      id: 'badge_counter_quote_status',
      children: 'Quote Not Sent',
      contrast: 'low',
      color: 'neutral',
    },
    counter_quote: {
      ranges: {
        jr_cost: '$25.99',
        plus_cost: '$29.99',
      },
      moq: '100',
      mcq: '50',
      fabric_content: 'Cotton 100%',
      lead_time_in_weeks: '4 weeks',
      ro_lead_time_in_weeks: '3 weeks',
    },
    coo: 'China',
    shipment_mode: 'Air',
    fabric_weight: '180 gsm',
    fabric_name: 'Premium Cotton',
    fabric_type: 'Woven',
  },
  {
    id: '2',
    photos: [
      'https://cdn.shopify.com/s/files/1/0293/9277/files/11-19-24_S7_68_ZDF01F440029_BlackWash_CZ_DJ_11-33-22_43875_PXF.jpg',
      'https://cdn.shopify.com/s/files/1/0293/9277/products/08-31-21Studio4_RT_RL_15-25-39_121_FNMH880_BlackWash_8488_PB.jpg',
      'https://cdn.shopify.com/s/files/1/0293/9277/products/NOVABEAUTYD.T.F.PreciseBrowPencil-Blonde_mer.jpg',
    ],
    priority: {
      id: 'badge_priority',
      children: 'PreseasonQ1Q2',
      contrast: 'low',
      color: 'neutral',
    },
    style_color: 'Black/S002',
    division: 'Men',
    display_name: 'Winter Jacket',
    alias: 'Cold Weather Series',
    key_details: 'Water-resistant, insulated',
    counter_sample_status: {
      id: 'badge_counter_sample_status',
      children: 'Shipped',
      contrast: 'low',
      color: 'blue',
    },
    counter_quote_status: {
      id: 'badge_counter_quote_status',
      children: 'Quote Sent',
      contrast: 'low',
      color: 'green',
    },
    counter_quote: {
      ranges: {
        jr_cost: '$45.99',
        plus_cost: '$49.99',
      },
      moq: '200',
      mcq: '100',
      fabric_content: 'Polyester 80%, Cotton 20%',
      lead_time_in_weeks: '6 weeks',
      ro_lead_time_in_weeks: '4 weeks',
    },
    coo: 'Vietnam',
    shipment_mode: 'Sea',
    fabric_weight: '250 gsm',
    fabric_name: 'DuraShield',
    fabric_type: 'Woven',
  },
];

const renderLastMessage = () => (
  <div className="flex flex-col gap-1">
    <P size="xs" color="disabled" className="select-none">
      8m ago
    </P>
    <P size="xs">Asperiores ut nihil incidunt commodi ea veritatis.</P>
  </div>
);

const renderTeamMembers = () => (
  <AvatarGroup max={3}>
    <Avatar name="John Doe" badgeType="vendor" />
    <Avatar name="Jane Smith" badgeType="vendor" />
  </AvatarGroup>
);

const renderFnContacts = () => (
  <AvatarGroup max={3}>
    <Avatar name="Alice Johnson" badgeType="fn_internal" />
  </AvatarGroup>
);

const renderVendorSampleShipped = (itemId: string) =>
  itemId === '2' ? (
    'Jan 15, 2023'
  ) : (
    <div>
      <Button size="sm" color="secondary">
        Mark As Shipped
      </Button>
    </div>
  );

const renderVendorSampleTracking = (itemId: string) =>
  itemId === '2' ? (
    <Clickable>
      <P size="xsm" weight="normal" color="purple">
        View Tracking
      </P>
    </Clickable>
  ) : null;

const renderActions = () => (
  <Button size="sm" color="secondary">
    Actions
  </Button>
);

const renderVendorSampleReceived = (itemId: string) =>
  itemId === '2' ? 'Jan 20, 2023' : null;

const getFormattedDate = (date?: string | null) => {
  if (!date) return '-';

  return DateTime.fromISO(date).toFormat('MM/dd/yyyy');
};

export const mapInspoToRows = (callbacks?: {
  onPhotoClick?: (images: string[]) => void;
}) =>
  mockData.map((item) => ({
    [InspoTableColumns.ID]: item.id,
    [InspoTableColumns.PHOTO]: {
      alt: 'photo',
      photo: item.photos[0],
      onClick: () => callbacks?.onPhotoClick?.(item.photos),
    },
    [InspoTableColumns.PRIORITY]: item.priority,
    [InspoTableColumns.ALIAS]: item.alias,
    [InspoTableColumns.REQUESTED_ON]: getFormattedDate(
      new Date().toISOString(),
    ),
    [InspoTableColumns.KEY_DETAILS]: item.key_details,
    [InspoTableColumns.VENDOR_SAMPLE_STATUS]: item.counter_sample_status,
    [InspoTableColumns.QUOTE_STATUS]: item.counter_quote_status,
    [InspoTableColumns.JR_COST]: item.counter_quote.ranges.jr_cost,
    [InspoTableColumns.PLUS_COST]: item.counter_quote.ranges.plus_cost,
    [InspoTableColumns.MOQ]: item.counter_quote.moq,
    [InspoTableColumns.MCQ]: item.counter_quote.mcq,
    [InspoTableColumns.COO]: item.coo,
    [InspoTableColumns.SHIPMENT_MODE]: item.shipment_mode,
    [InspoTableColumns.FABRIC_CONTENT]: item.counter_quote.fabric_content,
    [InspoTableColumns.FABRIC_WEIGHT]: item.fabric_weight,
    [InspoTableColumns.FABRIC_CODE_AND_NAME]: item.fabric_name,
    [InspoTableColumns.FABRIC_TYPE]: item.fabric_type,
    [InspoTableColumns.LEAD_TIME]: item.counter_quote.lead_time_in_weeks,
    [InspoTableColumns.REORDER_LEAD_TIME]:
      item.counter_quote.ro_lead_time_in_weeks,
    [InspoTableColumns.LAST_MESSAGE]: renderLastMessage(),
    [InspoTableColumns.TEAM_MEMBERS]: renderTeamMembers(),
    [InspoTableColumns.FN_CONTACTS]: renderFnContacts(),
    [InspoTableColumns.VENDOR_SAMPLE_SHIPPED]: renderVendorSampleShipped(
      item.id,
    ),
    [InspoTableColumns.VENDOR_SAMPLE_TRACKING]: renderVendorSampleTracking(
      item.id,
    ),
    [InspoTableColumns.VENDOR_SAMPLE_RECEIVED]: renderVendorSampleReceived(
      item.id,
    ),
    [InspoTableColumns.ACTIONS]: renderActions(),
  }));
